/**
 * admin.module.css — the operator dashboard. `docs/plan.md` T46,
 * `docs/design-direction.md` §5 (spacing/radii/borders), §8's screen contract and
 * §8.6's amp-faceplate spec-strip language, reapplied to a control surface.
 *
 * No shadow anywhere (§5.5) — every separation is a 1px `--border*` rule. The only
 * radius in this file is `var(--r-cut)` (`0`); nothing here is a pill (§5.3). Every
 * colour is a semantic alias from `tokens.css` §3.2 — never a raw `--ink-*` /
 * `--paper-*` / `--warn` token — so `--danger-text`/`--danger-fill` are how the one
 * `--warn` ink enters this file, exactly as the contract requires.
 */

.masthead {
  padding-inline: var(--gutter);
  padding-block: var(--s4) var(--s3);
  display: flex;
  flex-direction: column;
  gap: var(--s1);
}

.subtitle {
  color: var(--text-3);
}

/* Cyrillic copy in the tracked mono tier drops the tracking and stops shouting —
   the same recipe every other module in the app uses (§4.3, §12.16). */
:global(.t-mono).cyr {
  letter-spacing: 0.02em;
  text-transform: none;
}

.section {
  display: flex;
  flex-direction: column;
  gap: var(--s3);
}

.inlineStatus {
  display: flex;
  align-items: center;
  gap: var(--s3);
  color: var(--text-3);
}

.errorLine {
  color: var(--danger-text);
}

.retryButton {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: start;
  min-inline-size: var(--drop);
  min-block-size: var(--drop);
  padding-inline: var(--s4);
  color: var(--text);
  background: transparent;
  border: 1px solid var(--border-strong);
  border-radius: var(--r-cut);
}

.retryButton:active {
  background: var(--bg-pressed);
}

/* The drift plate — the one place besides an ErrorState this app prints --warn. */
.drift {
  display: inline-flex;
  align-items: center;
  align-self: start;
  gap: var(--s2);
  padding: var(--s1) var(--s3);
  color: var(--danger-text);
  border: 1px solid var(--danger-text);
  border-radius: var(--r-cut);
}

.reindexGrid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--s2);
}

.reindexButton {
  min-block-size: var(--drop);
  padding-inline: var(--s3);
  color: var(--text);
  background: var(--bg-raised);
  border: 1px solid var(--border-strong);
  border-radius: var(--r-cut);
}

.reindexButton:active {
  background: var(--bg-pressed);
}

.reindexButton:disabled {
  color: var(--text-3);
  background: var(--bg-panel);
}

.jobList {
  display: flex;
  flex-direction: column;
  gap: 0;
  list-style: none;
  margin: 0;
  padding: 0;
}

.jobRow {
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: var(--s3);
  padding-block: var(--s2);
  border-block-end: 1px solid var(--border);
}

.jobState {
  color: var(--text-3);
}

.jobProgress {
  display: flex;
  align-items: center;
  justify-self: end;
}

.emptyLine {
  color: var(--text-3);
}

.cacheBlock {
  display: flex;
  flex-direction: column;
  gap: var(--s3);
}

.evictButton {
  align-self: start;
  min-inline-size: var(--drop);
  min-block-size: var(--drop);
  padding-inline: var(--s4);
  color: var(--text);
  background: transparent;
  border: 1px solid var(--border-strong);
  border-radius: var(--r-cut);
}

.evictButton:active {
  background: var(--bg-pressed);
}

.evictButton[data-armed='true'] {
  color: var(--text-on-accent);
  background: var(--danger-fill);
  border-color: var(--danger-fill);
}

.freedLine {
  color: var(--text-3);
}
